import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { ResourceRecentItem } from "@k8s-dashboard/contracts";
import { fetchRecentResources } from "@/api/queries";
import { readFavorites, writeFavorites } from "@/app/preference-storage";
import type { RecentRef } from "@/app/recent";
import { useT } from "@/i18n/I18nProvider";

const objectId = (value: Pick<RecentRef, "group" | "version" | "resource" | "uid">) =>
  `${value.group}/${value.version}/${value.resource}/${value.uid}`;

export function FavoritesDisclosure({ partition, clusterId, navSearch, generation }: {
  partition: string;
  clusterId: string;
  navSearch: string;
  generation: string;
}) {
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState<{ epoch: number; generation: string; refs: RecentRef[] }>();
  const [visibleRefs, setVisibleRefs] = useState<RecentRef[]>([]);
  const navigate = useNavigate();
  const { t } = useT("nav");
  useEffect(() => {
    setOpen(false);
    setLoad(undefined);
    setVisibleRefs([]);
  }, [clusterId, generation, partition]);
  const currentLoad = load?.generation === generation ? load : undefined;
  const query = useQuery({
    queryKey: ["favorite-resources", partition, clusterId, generation, currentLoad?.epoch ?? 0],
    queryFn: ({ signal }) => fetchRecentResources(clusterId, currentLoad?.refs ?? [], signal),
    enabled: open && Boolean(currentLoad?.refs.length),
    meta: { streamInvalidation: "manual" },
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const resolved = open && currentLoad && query.isSuccess && !query.isFetching ? query.data : undefined;
  useEffect(() => {
    if (!resolved || !currentLoad) return;
    const alive = new Set(resolved.map(objectId));
    const next = currentLoad.refs.filter((ref) => alive.has(objectId(ref)));
    if (next.length !== currentLoad.refs.length) {
      writeFavorites({ partition, clusterId }, next);
      setVisibleRefs(next);
    }
  }, [clusterId, currentLoad, partition, resolved]);
  const currentVisibleRefs = currentLoad ? visibleRefs : [];
  const visible = useMemo(() => {
    if (!resolved) return [];
    const allowed = new Set(currentVisibleRefs.map(objectId));
    return resolved.filter((item) => allowed.has(objectId(item))).slice(0, 20);
  }, [currentVisibleRefs, resolved]);
  const go = (item: ResourceRecentItem) => {
    const params = new URLSearchParams(navSearch);
    params.set("res", `${item.group}/${item.version}/${item.resource}`);
    if (item.namespace) params.set("ns", item.namespace); else params.delete("ns");
    params.set("item", `${item.namespace ?? ""}/${item.name}/${item.uid}`);
    navigate({ pathname: "/resources", search: `?${params.toString()}` });
  };
  const remove = (item: ResourceRecentItem) => {
    const next = currentVisibleRefs.filter((ref) => objectId(ref) !== objectId(item));
    if (writeFavorites({ partition, clusterId }, next)) setVisibleRefs(next);
  };
  return (
    <details className="app__favorites" open={open} onToggle={(event) => {
      const nextOpen = event.currentTarget.open;
      setOpen(nextOpen);
      if (nextOpen) {
        const refs = readFavorites({ partition, clusterId });
        setVisibleRefs(refs);
        setLoad({ epoch: Date.now(), generation, refs });
      } else {
        setLoad(undefined);
        setVisibleRefs([]);
      }
    }}>
      <summary>{t("nav.favorites.title")}</summary>
      {open && currentVisibleRefs.length === 0 && <p className="muted">{t("nav.favorites.empty")}</p>}
      {open && currentVisibleRefs.length > 0 && query.isFetching && <p className="muted">{t("nav.favorites.loading")}</p>}
      {open && currentVisibleRefs.length > 0 && query.isError && <p role="alert">{t("nav.favorites.error")}</p>}
      {visible.length > 0 && (
        <ul className="app__favorites-list">
          {visible.map((item) => (
            <li key={objectId(item)}>
              <button type="button" className="linkish" onClick={() => go(item)}>{item.kind} · {item.name}</button>
              <button type="button" className="linkish" onClick={() => remove(item)} aria-label={t("nav.favorites.remove", { name: item.name })}>×</button>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
